/**
 * IndexedDB store for the model-property data shown in the viewer.
 *
 * Two object stores so the static AWS data (model tree) persists across viewer sessions while the dynamic
 * Mongo data (custom metadata, quantities, asset data) is dropped on viewer close / before next fetch.
 *
 *   modelPropertiesStaticData   keyPath ["fileVersionId", "nodeId"], one row per node, NO indexes — the
 *     compound primary key already orders a file's rows contiguously, so per-node lookup is
 *     `store.get([fvId, nodeId])` and file-scoped reads/deletes use a primary-key range
 *     (`staticFileRange`). Dropping the old `fileVersionId` index halves per-row bulk-insert cost.
 *   modelPropertiesDynamicData  keyPath autoIncrement (one row per Mongo doc), indexes fileVersionId and
 *     byNode ([fileVersionId, nodeId]).
 *   modelPropertiesMetaData  keyPath fileVersionId: the version's `conversionEndTime` when its static rows were
 *     written, so a reconverted file's rows are refetched (`processOneFileVersion` in actions/Viewer.ts).
 *
 * No in-memory cache — every read goes through IndexedDB, and the merged view is computed at read time by
 * `aggregateDynamicDocs` + `mergeNode`.
 */

import * as Sentry from "@Sentry";
import get from "lodash/get";
import toPath from "lodash/toPath";
import { showToastNotification } from "@Reducers/toastSlice";
import { TOAST_TYPE_WARNING, CAD_ENGINE } from "@Services/constants";
import { hasIndexedDbSpace, isStorageFullError } from "@Services/utils/indexedDbStorage";
import { updateUnits, runWithConcurrency, ObjectTreeFieldsMinValuesMapper } from "@Services/utils/helpers";
import { resolveFileVersionIds } from "@Services/utils/resolveFileVersionIds";
import { store } from "@Store";
import { t } from "@i18n";
import { DOCUMENT_TYPE } from "@Helpers/index";
import type { PortalUnits } from "@Actions/Viewer";
import type { ApiRow } from "@AxiosWrapper";
import type { JSONType } from "zod";

const DB_NAME = "modelPropertiesData";
const STATIC_STORE = "modelPropertiesStaticData";
const DYNAMIC_STORE = "modelPropertiesDynamicData";
const META_STORE = "modelPropertiesMetaData";
const LEGACY_STORE = "modelPropertiesData";
const DB_VERSION = 6;

// Rows per `getAll` when reading projected static data. Bigger = fewer IDB
// round-trips (the dominant cost for large trees) but a larger transient
// working set — up to this many freshly-deserialized rows (incl. their heavy
// `properties` blobs) are alive per in-flight file until they're projected and
// GC'd. 5k keeps each batch's synchronous projection well under a long-task
// while cutting round-trips ~5000x vs. a one-row-at-a-time cursor.
const PROJECTED_STATIC_BATCH_SIZE = 5000;

/**
 * One model-tree node: a row of the archive `/queries/queryModelProperties` streams as zip bytes (its JSON
 * twin returns the same rows as `result`). The static store's keyPath is `[fileVersionId, nodeId]`, so a
 * stored row always carries `fileVersionId`; `processOneFileVersion` drops archive rows lacking either key.
 */
export type StaticRow = ApiRow<"/queries/queryModelProperties"> & { fileVersionId: string };

/**
 * One `/queries/queryFileProperties` overlay row, stamped with the `fileVersionId` string the dynamic
 * store's `fileVersionId` and `byNode` indexes key on (`processOneFileVersion` and the per-node mutation
 * thunks set it). The stamp replaces the wire row's own optional, server-validated id: it is the id the
 * client asked for or edited, the store's key, not a value the server returned. `data` is the
 * heterogeneous Mongo payload — a quantity object, a custom-properties object, or an array of
 * property-path strings (e.g. `properties.<name>`) depending on DOCUMENT_TYPE — narrowed at each use site.
 */
export type DynamicRow = Omit<ApiRow<"/queries/queryFileProperties">, "fileVersionId"> & { fileVersionId: string };

export type MergedNode = {
  nodeId?: number | string;
  fileVersionId?: string;
  parent?: number | string | null;
  properties?: Record<string, unknown>;
  customProperties?: Record<string, unknown>;
  assetData?: unknown;
  [key: string]: unknown;
};
type FileNodes = Record<string, MergedNode>;
type AllData = Record<string, FileNodes>;

/**
 * Forward minify map: original string -> small integer id (1-based). Produced by
 * `getModelPropertiesProjectedStaticData` when `convertToMinifiedData` is on.
 * fileId and fileVersionId share a single id space (`fileIdAndVersionIdMinMapper`),
 * "CUBE Name" gets its own (`cubeNameMinMapper`). Consumers build the inverse
 * (id -> string) to rehydrate nodes — see `getNodeData` in the ModelTree.
 */
export type FieldValueMinMapper = Record<string, number>;

/** Return shape of `getModelPropertiesProjectedStaticData`. */
export type ProjectedStaticData = {
  /**
   * `{ [fileVersionKey]: { [nodeId]: projectedNode } }`. When minified, the file
   * key is the fileVersionId's integer id (from `fileIdAndVersionIdMinMapper`)
   * and each node's fields are keyed by `ObjectTreeFieldsMinValuesMapper` codes
   * with fileId/fileVersionId/"CUBE Name" values stored as mapper ids. When not
   * minified, keys are the raw fileVersionId and full field names.
   */
  data: AllData;
  /** original fileId / fileVersionId -> integer id (empty when not minified). */
  fileIdAndVersionIdMinMapper: FieldValueMinMapper;
  /** original "CUBE Name" -> integer id (empty when not minified). */
  cubeNameMinMapper: FieldValueMinMapper;
};

export type MergeOptions = {
  /** When supplied, properties + typeProperties are run through `updateUnits`. */
  unitsNew?: Record<string, unknown> | undefined;
  portalUnits?: PortalUnits | null | undefined;
  /**
   * Engine type (e.g. "CAD", "BIM"). Used to handle the legacy CAD path where
   * AWS returns no model tree and the merged shape comes from flattening
   * `data` into each Mongo doc — see `mergeNode`.
   */
  engineType?: string | undefined;
  originalFileVersionId?: string | undefined;
};

let dbInstance: IDBDatabase | null = null;
let openPromise: Promise<IDBDatabase> | null = null;

// ----------------------------------------------------------------------------
// DB lifecycle
// ----------------------------------------------------------------------------

/** Drop the cached handle so the next `openDB()` reconnects instead of reusing a corpse. */
function forget(db: IDBDatabase): void {
  if (dbInstance === db) dbInstance = null;
}

/**
 * True while `db` can still start transactions. The browser retires connections on
 * its own — another tab's versionchange, storage eviction, a backing-store failure
 * (every DB of an origin shares one backing store, so a fatal write error here takes
 * the others down too) — and exposes no flag to read. The only way to ask is to start
 * a throwaway transaction: it throws InvalidStateError once the connection is closing.
 * An unused transaction auto-commits without touching disk, so the probe is free next
 * to the real read it guards.
 */
function isUsable(db: IDBDatabase): boolean {
  try {
    db.transaction(STATIC_STORE, "readonly");
    return true;
  } catch {
    return false;
  }
}

function openDB(): Promise<IDBDatabase> {
  if (dbInstance && isUsable(dbInstance)) return Promise.resolve(dbInstance);
  dbInstance = null;
  // Dedupe concurrent opens: several readers race here on a viewer load, and each
  // would otherwise start its own connection, leaking every one but the last.
  openPromise ??= new Promise<IDBDatabase>((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onerror = () => {
      openPromise = null;
      reject(request.error);
    };
    request.onsuccess = () => {
      const db = request.result;
      // Step aside so another tab can upgrade, and drop the handle either way:
      // `close` fires only on a forced close, never after an explicit close().
      db.onversionchange = () => {
        db.close();
        forget(db);
      };
      db.onclose = () => forget(db);
      dbInstance = db;
      openPromise = null;
      resolve(db);
    };
    request.onupgradeneeded = () => {
      const db = request.result;
      const upgradeTx = request.transaction;

      // v1 stored merged data in a single store keyed by fileVersionId. We
      // can't reliably split that into static/dynamic, so drop it. Next fetch
      // re-populates both stores from the source-of-truth services.
      if (db.objectStoreNames.contains(LEGACY_STORE)) {
        db.deleteObjectStore(LEGACY_STORE);
      }

      if (!db.objectStoreNames.contains(STATIC_STORE)) {
        // No secondary index: the compound primary key `[fileVersionId, nodeId]`
        // groups a file's rows contiguously, so file-scoped reads/deletes use a
        // primary-key range (`staticFileRange`) instead of a `fileVersionId`
        // index. Skipping the index halves per-row write cost on bulk inserts.
        db.createObjectStore(STATIC_STORE, { keyPath: ["fileVersionId", "nodeId"] });
      }
      // v6. Rows cached before it have no end time, which reads as a version that had none (`getStaticDataState`).
      if (!db.objectStoreNames.contains(META_STORE)) {
        db.createObjectStore(META_STORE, { keyPath: "fileVersionId" });
      }

      let dynamicStore: IDBObjectStore;
      if (!db.objectStoreNames.contains(DYNAMIC_STORE)) {
        dynamicStore = db.createObjectStore(DYNAMIC_STORE, { autoIncrement: true });
        dynamicStore.createIndex("fileVersionId", "fileVersionId", { unique: false });
      } else if (upgradeTx) {
        dynamicStore = upgradeTx.objectStore(DYNAMIC_STORE);
      } else {
        // The IndexedDB spec sets `request.transaction` to the versionchange transaction for the whole
        // `upgradeneeded` dispatch (lib.dom types it nullable for every request), so null is a browser bug.
        // The existing store cannot be reached without it: report, and leave the store as it is.
        Sentry.captureException(
          new Error(`modelPropertiesDataDB.openDB: upgradeneeded without a versionchange transaction (v${DB_VERSION})`),
        );
        return;
      }
      if (!dynamicStore.indexNames.contains("byNode")) {
        dynamicStore.createIndex("byNode", ["fileVersionId", "nodeId"], { unique: false });
      }
    };
  });
  return openPromise;
}

// ----------------------------------------------------------------------------
// Storage-quota guard
// ----------------------------------------------------------------------------

export async function ensureIndexedDbSpace(payload: unknown): Promise<boolean> {
  // Cheap byte estimate, avoiding a full serialization of large payloads:
  //  - number: caller already computed a rough size — use it as-is.
  //  - non-empty array: size one sampled row and scale by length, instead of
  //    stringifying every row (the per-row size is measured with the same
  //    Blob/JSON.stringify path used below, just on a single element).
  //  - anything else: fall back to a full estimate.
  let requiredBytes: number;
  if (typeof payload === "number") {
    requiredBytes = payload;
  } else if (Array.isArray(payload) && payload.length > 0) {
    requiredBytes = JSON.stringify(payload[0]).length * payload.length * 2;
  } else {
    requiredBytes = JSON.stringify(payload).length * 2;
  }

  const hasSpace = await hasIndexedDbSpace(requiredBytes);
  if (!hasSpace) {
    notifyStorageFull();
    return false;
  }
  return true;
}

const STORAGE_FULL_TOAST_INTERVAL_MS = 2 * 60 * 1000;
let lastStorageFullToastAt = 0;

function notifyStorageFull(): void {
  const now = Date.now();
  if (now - lastStorageFullToastAt < STORAGE_FULL_TOAST_INTERVAL_MS) return;
  lastStorageFullToastAt = now;
  store.dispatch(
    showToastNotification({
      content: t("Insufficient storage. Open Settings > Clear Cache to free space."),
      type: TOAST_TYPE_WARNING,
    }),
  );
}

export function reportModelDataLoadError(error: unknown): void {
  if (isStorageFullError(error)) {
    notifyStorageFull();
    return;
  }
  Sentry.captureException(error);
}

// ----------------------------------------------------------------------------
// Merge logic (mirrors `aggregateProps` + `mergeQuantityIntoProps`)
// ----------------------------------------------------------------------------

/** An IndexedDB read (`unknown`) that can be checked key by key. `DynamicRow.data` goes through `jsonRecord`. */
function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

/**
 * A `DynamicRow.data` payload as the key/value bag spreading it gives: an object as is, an array by its
 * indices (as `{ ...array }` would), undefined for a scalar or no payload. Shared with the mutation thunks
 * in `reducers/Viewer.ts`, so a row is read the same way where it is written and where it is merged.
 */
export function jsonRecord(value: JSONType | undefined): Record<string, JSONType> | undefined {
  if (typeof value !== "object" || value === null) return undefined;
  return Array.isArray(value) ? Object.fromEntries(Object.entries(value)) : value;
}

function aggregateDynamicDocs(docs: DynamicRow[]) {
  let quantityData: Record<string, JSONType> = {};
  let customPropertiesData: Record<string, JSONType> = {};
  // An array of property-path strings (e.g. `properties.<name>`), not a record — the seed is `[]` and the
  // only writer below assigns the DOCUMENT_TYPE_ASSETDATA doc's array payload.
  let assetData: unknown = [];

  for (const doc of docs) {
    if (!doc.data) continue;
    if (doc.DOCUMENT_TYPE === DOCUMENT_TYPE.DOCUMENT_TYPE_QUANTITY) {
      quantityData = { ...quantityData, ...jsonRecord(doc.data) };
    } else if (
      doc.DOCUMENT_TYPE === DOCUMENT_TYPE.DOCUMENT_TYPE_CUSTOM_PROPERTIES ||
      doc.DOCUMENT_TYPE === DOCUMENT_TYPE.DOCUMENT_TYPE_DRAWING_PROPERTIES
    ) {
      const custom = jsonRecord(doc.data);
      if (custom) customPropertiesData = custom;
    } else if (doc.DOCUMENT_TYPE === DOCUMENT_TYPE.DOCUMENT_TYPE_ASSETDATA) {
      assetData = doc.data;
    }
  }
  return { quantityData, customPropertiesData, assetData };
}

/** Matches the legacy `mergeQuantityIntoProps` from `mergeNodesIntoResponse`. */
function mergeQuantityIntoProps(
  nodeProps: Record<string, JSONType>,
  quantityData: Record<string, JSONType>,
): Record<string, JSONType> {
  // No quantity for this node (the common case): skip the spread and return the
  // existing props as-is instead of allocating a copy per node. The caller
  // passes a freshly-read, unshared IDB row, and the only downstream consumer
  // (`updateUnits`) is non-mutating, so reusing the reference is safe.
  if (Object.keys(quantityData).length === 0) return nodeProps;
  const merged = { ...nodeProps, ...quantityData };
  // A null Quantity / Unit falls back to the node's own; neither side having one leaves the key unset.
  for (const key of ["Quantity", "Unit"]) {
    const value = quantityData[key] ?? nodeProps[key];
    if (value === undefined) delete merged[key];
    else merged[key] = value;
  }
  return merged;
}

/** Matches the legacy filter from `mergeNodesIntoResponse`:
 *  AWS nodes are only kept when they're the root (nodeId 0) or have a parent. */
function passesAwsFilter(row: StaticRow): boolean {
  return row.nodeId === 0 || row.parent !== null;
}

/** Re-expresses a node's `properties` or `typeProperties` in the file's units (step 4 of `mergeNode`). */
export type UnitConversion = (props: Record<string, JSONType>) => Record<string, JSONType>;

/**
 * Builds the merged node from one static row + its dynamic docs. Mirrors `mergeNodesIntoResponse` in
 * `actions/Viewer.ts`:
 *   1. Drops AWS rows that fail the parent/root filter.
 *   2. Aggregates dynamic docs (quantity merged across docs; custom & asset data are last-wins per
 *      DOCUMENT_TYPE).
 *   3. Merges quantity fields into the AWS properties via `mergeQuantityIntoProps`.
 *   4. When `unitsNew` is supplied, runs both `properties` and `typeProperties` through `updateUnits`, or
 *      through `convert` when the caller reads only some of their values (getModelPropertyValues).
 *   5. Spreads the AWS node first (or, if absent, the docs' shared `fileVersionId`/`nodeId`), then
 *      overrides `properties`, `customProperties`/drawing data and `assetData`.
 */
function mergeNode(
  staticRow: StaticRow | undefined,
  dynamicRows: DynamicRow[],
  options?: MergeOptions,
  convert?: UnitConversion,
): MergedNode | undefined {
  // 1. AWS filter — drop rows that aren't the root and have parent === null.
  const effectiveStatic = staticRow && passesAwsFilter(staticRow) ? staticRow : undefined;
  if (!effectiveStatic && dynamicRows.length === 0) return undefined;

  // 1a. CAD with no AWS tree — flatten each Mongo doc as `{ ...rest, ...data }`
  // and accumulate. Mirrors the legacy
  //   responseData[fvId] = mongoData.data.reduce((acc, { data, ...rest }) =>
  //     ((acc[rest.nodeId] = { ...rest, ...data }), acc), {})
  // branch in `processOneFileVersion`.
  if (!effectiveStatic && options?.engineType === CAD_ENGINE) {
    let cadNode: Record<string, unknown> = {};
    for (const doc of dynamicRows) {
      const { data, ...rest } = doc;
      cadNode = { ...cadNode, ...rest, ...jsonRecord(data) };
    }
    return cadNode;
  }

  // 2. Dynamic aggregation.
  const { quantityData, customPropertiesData, assetData } = aggregateDynamicDocs(dynamicRows);

  // 3. Quantity merged into AWS properties.
  const propertiesWithCubeData = mergeQuantityIntoProps(effectiveStatic?.properties ?? {}, quantityData);

  // 4. Optional unit conversion (matches the legacy `if (unitsNew && Object.keys(unitsNew).length)` branch).
  const unitsNew = options?.unitsNew;

  if (effectiveStatic) {
    if (unitsNew && Object.keys(unitsNew).length > 0) {
      const toFileUnits = convert ?? ((props) => updateUnits(props, unitsNew, options.portalUnits));
      effectiveStatic.properties = toFileUnits(propertiesWithCubeData);
      effectiveStatic.typeProperties = toFileUnits(effectiveStatic.typeProperties ?? {});
    } else {
      effectiveStatic.properties = propertiesWithCubeData;
    }
  }

  // 5. Final shape — same field order as `mergeNodesIntoResponse`. A node with no static row (e.g. a
  // DWG constituent of a merged file, which is always BIM) takes its identity from its docs.
  const [firstDoc] = dynamicRows;
  return {
    ...(effectiveStatic ?? (firstDoc && { fileVersionId: firstDoc.fileVersionId, nodeId: firstDoc.nodeId })),
    properties: effectiveStatic?.properties ?? {},
    ...customPropertiesData,
    assetData,
  };
}

function groupDynamicByNode(rows: DynamicRow[]): Record<string, DynamicRow[]> {
  const map: Record<string, DynamicRow[]> = {};
  for (const row of rows) {
    const key = String(row.nodeId);
    (map[key] ??= []).push(row);
  }
  return map;
}

// ----------------------------------------------------------------------------
// Static (AWS) writes
// ----------------------------------------------------------------------------

/**
 * Primary-key range covering every row for one file in the static store, whose
 * key is `[fileVersionId, nodeId]`. An empty array `[]` sorts after any number
 * or string in IndexedDB's key ordering, so `[fileVersionId, []]` is greater
 * than `[fileVersionId, <anyNodeId>]` — the range matches all nodes under the
 * file. Replaces the dropped `fileVersionId` index for file-scoped operations.
 */
function staticFileRange(fileVersionId: string): IDBKeyRange {
  return IDBKeyRange.bound([fileVersionId], [fileVersionId, []]);
}

export async function setStaticDataForFile(
  fileVersionId: string,
  rows: StaticRow[],
  size: number,
  conversionEndTime?: number | null,
): Promise<void> {
  const hasSpace = await ensureIndexedDbSpace(size);
  const db = await openDB();
  return new Promise((resolve, reject) => {
    // "relaxed" durability: don't force a disk fsync before `oncomplete` fires.
    // The commit (the slow part of a bulk insert) reports done as soon as the
    // data is buffered, not after it's physically flushed. Safe here because a
    // rare lost flush just re-triggers the refetch in `processOneFileVersion`.
    const tx = db.transaction([STATIC_STORE, META_STORE], "readwrite", { durability: "relaxed" });
    const objectStore = tx.objectStore(STATIC_STORE);
    const metaStore = tx.objectStore(META_STORE);

    // Clear this file's existing rows with one range delete (queued before the
    // puts, so it runs first), then insert the fresh rows. No getAllKeys round
    // trip and no per-key delete loop.
    objectStore.delete(staticFileRange(fileVersionId));
    if (hasSpace) {
      for (const row of rows) objectStore.put(row);
      metaStore.put({ fileVersionId, conversionEndTime: conversionEndTime ?? null });
    } else {
      metaStore.delete(fileVersionId);
    }

    tx.oncomplete = () => resolve();
    // No `tx.onerror` in this file: it runs before the abort sets `tx.error`, so it rejected with null.
    tx.onabort = () => reject(new Error("IndexedDB transaction aborted", { cause: tx.error }));
  });
}

// ----------------------------------------------------------------------------
// Dynamic (Mongo) writes
// ----------------------------------------------------------------------------

/** Replaces all dynamic rows for `fileVersionId` with the supplied rows. */
export async function setDynamicDataForFile(fileVersionId: string, rows: DynamicRow[]): Promise<void> {
  if (!(await ensureIndexedDbSpace(rows))) return;
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(DYNAMIC_STORE, "readwrite");
    const objectStore = tx.objectStore(DYNAMIC_STORE);

    const keysReq = objectStore.index("fileVersionId").getAllKeys(fileVersionId);
    keysReq.onsuccess = () => {
      for (const k of keysReq.result) objectStore.delete(k);
      for (const row of rows) objectStore.add(row);
    };

    tx.oncomplete = () => resolve();
    tx.onabort = () => reject(new Error("IndexedDB transaction aborted", { cause: tx.error }));
  });
}

// ----------------------------------------------------------------------------
// Reads / existence checks
// ----------------------------------------------------------------------------

/** The two members every row in both stores is keyed on. */
function isNodeId(value: unknown): value is number | string {
  return typeof value === "number" || typeof value === "string";
}

/**
 * `IDBObjectStore.get`/`getAll` and `IDBIndex.getAll` are declared `IDBRequest<any>` / `IDBRequest<any[]>`
 * in lib.dom — IndexedDB cannot know what a store holds — so every read below takes its request as
 * `unknown` and narrows the result through these guards instead of asserting it. Both stores are private
 * to this module (`DB_NAME` is opened nowhere else) and every row is written by the write functions above,
 * which take `StaticRow`/`DynamicRow`; IndexedDB additionally refuses a row carrying no value at the static
 * store's keyPath, so the keyed pair is what is checked here. The `nodeId` check also admits a string,
 * wider than the routes' `number`; narrowing it would make reads skip a string-keyed row instead of
 * returning it.
 */
function isStaticRow(row: unknown): row is StaticRow {
  return isRecord(row) && typeof row.fileVersionId === "string" && isNodeId(row.nodeId);
}

function isDynamicRow(row: unknown): row is DynamicRow {
  return isRecord(row) && typeof row.fileVersionId === "string" && isNodeId(row.nodeId);
}

/** A `getAll` result. The array is handed back untouched when every row checks out, so a batched
 *  read keeps the row count its pagination keys off. */
function asStaticRows(rows: unknown[]): StaticRow[] {
  if (rows.every(isStaticRow)) return rows;
  return rows.filter(isStaticRow);
}

function asDynamicRows(rows: unknown[]): DynamicRow[] {
  if (rows.every(isDynamicRow)) return rows;
  return rows.filter(isDynamicRow);
}

export async function getStaticDataForFile(fileVersionId: string): Promise<StaticRow[]> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STATIC_STORE, "readonly");
    const req: IDBRequest<unknown[]> = tx.objectStore(STATIC_STORE).getAll(staticFileRange(fileVersionId));
    req.onerror = () => reject(req.error);
    req.onsuccess = () => resolve(asStaticRows(req.result));
  });
}

export async function getDynamicDataForFile(fileVersionId: string): Promise<DynamicRow[]> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(DYNAMIC_STORE, "readonly");
    const req: IDBRequest<unknown[]> = tx.objectStore(DYNAMIC_STORE).index("fileVersionId").getAll(fileVersionId);
    req.onerror = () => reject(req.error);
    req.onsuccess = () => resolve(asDynamicRows(req.result));
  });
}

/** Returns all dynamic rows for a single (fileVersionId, nodeId). */
export async function getDynamicDocsForNode(fileVersionId: string, nodeId: number | string): Promise<DynamicRow[]> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(DYNAMIC_STORE, "readonly");
    const req: IDBRequest<unknown[]> = tx.objectStore(DYNAMIC_STORE).index("byNode").getAll([fileVersionId, nodeId]);
    req.onerror = () => reject(req.error);
    req.onsuccess = () => resolve(asDynamicRows(req.result));
  });
}

/**
 * Replaces every dynamic row for a single (fileVersionId, nodeId) with the
 * supplied list. Used by per-node mutations (custom metadata, quantity,
 * asset data) — they read the node's docs, modify them, then call this to
 * commit.
 */
export async function replaceDynamicDocsForNode(
  fileVersionId: string,
  nodeId: number | string,
  docs: DynamicRow[],
): Promise<void> {
  if (!(await ensureIndexedDbSpace(docs))) return;
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(DYNAMIC_STORE, "readwrite");
    const objectStore = tx.objectStore(DYNAMIC_STORE);
    const keysReq = objectStore.index("byNode").getAllKeys([fileVersionId, nodeId]);
    keysReq.onsuccess = () => {
      for (const k of keysReq.result) objectStore.delete(k);
      for (const doc of docs) objectStore.add(doc);
    };
    tx.oncomplete = () => resolve();
    tx.onabort = () => reject(new Error("IndexedDB transaction aborted", { cause: tx.error }));
  });
}

/**
 * Batched read: each node's dynamic docs, index-aligned with `keys`, fetched in
 * ONE readonly transaction (vs one transaction per node). Used by the bulk
 * mutation thunks so a multi-node edit does a single read round-trip.
 */
export function getDynamicDocsForNodes(
  keys: Array<{ fileVersionId: string; nodeId: number | string }>,
): Promise<DynamicRow[][]> {
  if (keys.length === 0) return Promise.resolve([]);
  return openDB().then(
    (db) =>
      new Promise<DynamicRow[][]>((resolve, reject) => {
        const tx = db.transaction(DYNAMIC_STORE, "readonly");
        const idx = tx.objectStore(DYNAMIC_STORE).index("byNode");
        const rows: DynamicRow[][] = new Array<DynamicRow[]>(keys.length);
        keys.forEach(({ fileVersionId, nodeId }, i) => {
          const req: IDBRequest<unknown[]> = idx.getAll([fileVersionId, nodeId]);
          req.onsuccess = () => {
            rows[i] = asDynamicRows(req.result);
          };
        });
        tx.oncomplete = () => resolve(rows);
        tx.onabort = () => reject(new Error("IndexedDB transaction aborted", { cause: tx.error }));
      }),
  );
}

/**
 * Batched write: replaces the dynamic docs for several (fileVersionId, nodeId)
 * pairs in ONE readwrite transaction (vs one transaction per node). Each entry's
 * existing rows are deleted and its new `docs` added.
 */
export async function replaceDynamicDocsForNodes(
  entries: Array<{ fileVersionId: string; nodeId: number | string; docs: DynamicRow[] }>,
): Promise<void> {
  if (entries.length === 0) return;
  if (!(await ensureIndexedDbSpace(entries.flatMap((e) => e.docs)))) return;
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(DYNAMIC_STORE, "readwrite");
    const objectStore = tx.objectStore(DYNAMIC_STORE);
    const idx = objectStore.index("byNode");
    for (const { fileVersionId, nodeId, docs } of entries) {
      const keysReq = idx.getAllKeys([fileVersionId, nodeId]);
      keysReq.onsuccess = () => {
        for (const k of keysReq.result) objectStore.delete(k);
        for (const doc of docs) objectStore.add(doc);
      };
    }
    tx.oncomplete = () => resolve();
    tx.onabort = () => reject(new Error("IndexedDB transaction aborted", { cause: tx.error }));
  });
}

/** True if any static rows exist for this file in IDB. Stops at the first match. */
export async function hasStaticDataForFile(fileVersionId: string): Promise<boolean> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STATIC_STORE, "readonly");
    const req = tx.objectStore(STATIC_STORE).openKeyCursor(staticFileRange(fileVersionId));
    req.onerror = () => reject(req.error);
    req.onsuccess = () => resolve(req.result !== null);
  });
}

export async function getStaticDataState(
  fileVersionId: string,
): Promise<{ hasRows: boolean; conversionEndTime: number | null }> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction([STATIC_STORE, META_STORE], "readonly");
    let hasRows = false;
    let conversionEndTime: number | null = null;
    const rowReq = tx.objectStore(STATIC_STORE).openKeyCursor(staticFileRange(fileVersionId));
    rowReq.onsuccess = () => {
      hasRows = rowReq.result !== null;
    };
    const metaReq: IDBRequest<unknown> = tx.objectStore(META_STORE).get(fileVersionId);
    metaReq.onsuccess = () => {
      const meta = metaReq.result;
      if (isRecord(meta) && typeof meta.conversionEndTime === "number") conversionEndTime = meta.conversionEndTime;
    };
    tx.oncomplete = () => resolve({ hasRows, conversionEndTime });
    tx.onabort = () => reject(new Error("IndexedDB transaction aborted", { cause: tx.error }));
  });
}

/** True if any dynamic rows exist for this file in IDB. Stops at the first match. */
export async function hasDynamicDataForFile(fileVersionId: string): Promise<boolean> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(DYNAMIC_STORE, "readonly");
    const req = tx.objectStore(DYNAMIC_STORE).index("fileVersionId").openKeyCursor(IDBKeyRange.only(fileVersionId));
    req.onerror = () => reject(req.error);
    req.onsuccess = () => resolve(req.result !== null);
  });
}

/**
 * True when EITHER store has at least one row for the file. Existence-only
 * (key-cursor) — loads no row values. Used by `isFileModelDataWiped` to detect
 * a cache wipe by another tab.
 */
export async function hasFileModelData(fileVersionId: string): Promise<boolean> {
  const [hasStatic, hasDynamic] = await Promise.all([
    hasStaticDataForFile(fileVersionId),
    hasDynamicDataForFile(fileVersionId),
  ]);
  return hasStatic || hasDynamic;
}

// Per-tab, in-memory snapshot of which files actually had rows in IDB at the
// moment their load completed (ids only — no model data, no copies). Lets
// `isFileModelDataWiped` tell an EXTERNAL wipe (was present, now gone) apart
// from a file that legitimately has no model data — e.g. a CAD file with no AWS
// tree and no Mongo docs, where `hasFileModelData` is `false` even normally.
const filesPresentAtLoad = new Set<string>();

/** Record whether `fileVersionId` had rows in IDB when its load finished. */
export function markFileLoadState(fileVersionId: string, present: boolean): void {
  if (present) filesPresentAtLoad.add(fileVersionId);
  else filesPresentAtLoad.delete(fileVersionId);
}
export function clearFilesLoadState(): void {
  filesPresentAtLoad.clear();
}

/**
 * True when the file had data at load time (per {@link markFileLoadState}) but
 * its IDB rows are now gone — i.e. another tab's Settings > Clear Cache wiped
 * the shared, per-origin store underneath this tab. Files that never had data
 * (legitimately-empty CAD) return `false`. Existence-only; makes no data copies.
 */
export async function isFileModelDataWiped(fileVersionId: string): Promise<boolean> {
  if (!filesPresentAtLoad.has(fileVersionId)) return false;
  return !(await hasFileModelData(fileVersionId));
}

/**
 * Tab-level wipe check for a viewer file. Resolves a merged file to its
 * constituents (via `resolveFileVersionIds`) and reports a wipe when ANY
 * constituent that had data at load is now gone (see {@link isFileModelDataWiped}).
 * Files that never had data (e.g. legitimately-empty CAD) return `false`.
 * Existence-only; makes no data copies. Callers gate on load state — the hook
 * skips this while `isLoading` / before `isLoaded`.
 */
/** `fileVersionId` is absent when no file is open in the viewer — nothing is loaded, so nothing is wiped. */
export async function isModelPropertiesWiped(fileVersionId: string | undefined): Promise<boolean> {
  const ids = resolveFileVersionIds(store.getState().filesData, fileVersionId);
  if (ids.length === 0) return false;
  // A merged file is wiped if ANY constituent that had data is now gone.
  const results = await Promise.all(ids.map((id) => isFileModelDataWiped(id)));
  return results.some((wiped) => wiped);
}

/**
 * Reads static + dynamic for the file and returns the merged view.
 * `options` is optional — when omitted (or partially supplied), missing
 * fields are resolved from the Redux store via `resolveMergeOptions`.
 * This is the single resolution point — `getModelPropertiesData` and any
 * other caller that goes through here inherits the same behavior.
 */
async function getMergedDataForFile(fileVersionId: string, options?: MergeOptions): Promise<FileNodes> {
  const effectiveOptions = resolveMergeOptions(options);
  const [staticRows, dynamicRows] = await Promise.all([
    getStaticDataForFile(fileVersionId),
    getDynamicDataForFile(fileVersionId),
  ]);
  return mergeFileRows(staticRows, dynamicRows, effectiveOptions);
}

/** One file's merged view from all its rows: its static rows in key order, then the nodes only dynamic docs describe. */
export function mergeFileRows(staticRows: StaticRow[], dynamicRows: DynamicRow[], options: MergeOptions): FileNodes {
  const dynamicByNode = groupDynamicByNode(dynamicRows);
  const merged: FileNodes = {};

  for (const row of staticRows) {
    const key = String(row.nodeId);
    const m = mergeNode(row, dynamicByNode[key] ?? [], options);
    if (m) merged[key] = m;
    delete dynamicByNode[key];
  }
  // Nodes that have dynamic data but no matching static row.
  for (const [key, docs] of Object.entries(dynamicByNode)) {
    const m = mergeNode(undefined, docs, options);
    if (m) merged[key] = m;
  }
  return merged;
}

/**
 * Builds a complete `MergeOptions` by combining a partial caller-supplied override with values pulled from
 * the Redux store:
 *   - `unitsNew`    ← `state.filesData[fileVersionId].Units`
 *   - `engineType`  ← `state.filesData[fileVersionId].engineType`
 *   - `portalUnits` ← `state.portalUnits`
 * Explicit (non-`undefined`) fields in `override` always win, so direct callers of
 * `getMergedDataForNode` / `getModelPropertiesData` / `getNodeModelPropertiesData` can omit options and
 * still get the hook's merged-view behaviour.
 */
function resolveMergeOptions(options?: MergeOptions): MergeOptions {
  const fileId = options?.originalFileVersionId;
  if (!fileId) return options ?? {};

  const state = store.getState();
  const fileEntry = state.filesData[fileId];

  return {
    unitsNew: options.unitsNew ?? fileEntry?.Units,
    portalUnits: options.portalUnits ?? state.portalUnits,
    // A stored `null` engine type is no engine type.
    engineType: options.engineType ?? fileEntry?.engineType ?? undefined,
  };
}

/**
 * Reads the merged view for one or more nodes within a single file. `nodeId` can be a single id or an
 * array. Always returns `Record<nodeId, MergedNode>`; nodes with no data in either store are omitted.
 * `options` is optional — defaults are resolved from the Redux store via `resolveMergeOptions`. All reads
 * happen in a single IDB transaction.
 */
/**
 * Per-node reads issue two IDB requests per node (static `get` + dynamic `getAll`) inside one transaction.
 * To stop a very large selection (e.g. select-all) from queuing thousands of requests and retaining every
 * row at once, node lists longer than this are read in sequential chunks of this size.
 */
const MERGED_NODE_READ_CHUNK = 250;

export async function getMergedDataForNode(
  fileVersionId: string,
  nodeId: (number | string) | Array<number | string>,
  options?: MergeOptions,
): Promise<Record<string, MergedNode>> {
  const nodeIds = Array.isArray(nodeId) ? nodeId : [nodeId];
  if (nodeIds.length === 0) return {};

  // Resolve units/portalUnits/engineType once for the whole call (not per chunk).
  const effectiveOptions = resolveMergeOptions(options);

  if (nodeIds.length <= MERGED_NODE_READ_CHUNK) {
    return readMergedDocsForNodeChunk(fileVersionId, nodeIds, effectiveOptions);
  }

  const result: Record<string, MergedNode> = {};
  for (let i = 0; i < nodeIds.length; i += MERGED_NODE_READ_CHUNK) {
    const chunk = nodeIds.slice(i, i + MERGED_NODE_READ_CHUNK);
    Object.assign(result, await readMergedDocsForNodeChunk(fileVersionId, chunk, effectiveOptions));
  }
  return result;
}

/** One read transaction: static `get` + dynamic `getAll` per node, then merge. */
function readMergedDocsForNodeChunk(
  fileVersionId: string,
  nodeIds: Array<number | string>,
  effectiveOptions: MergeOptions,
): Promise<Record<string, MergedNode>> {
  return openDB().then(
    (db) =>
      new Promise<Record<string, MergedNode>>((resolve, reject) => {
        const tx = db.transaction([STATIC_STORE, DYNAMIC_STORE], "readonly");
        const staticStore = tx.objectStore(STATIC_STORE);
        const dynamicIdx = tx.objectStore(DYNAMIC_STORE).index("byNode");

        const staticRows: Record<string, StaticRow | undefined> = {};
        const dynamicRows: Record<string, DynamicRow[]> = {};

        for (const id of nodeIds) {
          const key = String(id);
          const staticReq: IDBRequest<unknown> = staticStore.get([fileVersionId, id]);
          const dynamicReq: IDBRequest<unknown[]> = dynamicIdx.getAll([fileVersionId, id]);
          staticReq.onsuccess = () => {
            const row = staticReq.result;
            staticRows[key] = isStaticRow(row) ? row : undefined;
          };
          dynamicReq.onsuccess = () => {
            dynamicRows[key] = asDynamicRows(dynamicReq.result);
          };
        }

        tx.oncomplete = () => {
          const result: Record<string, MergedNode> = {};
          for (const id of nodeIds) {
            const key = String(id);
            const merged = mergeNode(staticRows[key], dynamicRows[key] ?? [], effectiveOptions);
            if (merged) result[key] = merged;
          }
          resolve(result);
        };
        tx.onabort = () => reject(new Error("IndexedDB transaction aborted", { cause: tx.error }));
      }),
  );
}

/**
 * Reads merged data for one file (string) or several (string[], for merged files where the request carries
 * an array of source fileVersionIds). Result is wrapped as `{ [fvId]: ... }` so consumers keep using
 * `data[fileVersionId][nodeId]` access regardless of the input shape. Option resolution is handled inside
 * `getMergedDataForFile` per file.
 */
export async function getModelPropertiesData(
  options: Partial<MergeOptions> & { fileVersionId?: string | string[] },
): Promise<AllData> {
  const effectiveOptions = resolveMergeOptions(options);
  // Resolve which file versions to read: an explicit `fileVersionId` (an
  // already-resolved id or ids) wins; otherwise expand `originalFileVersionId`
  // — the viewer's file, where a merged file fans out to its constituent
  // versions — from the Redux store, so callers needn't resolve ids themselves.
  const ids =
    options.fileVersionId != null
      ? Array.isArray(options.fileVersionId)
        ? options.fileVersionId
        : [options.fileVersionId]
      : resolveFileVersionIds(store.getState().filesData, options.originalFileVersionId ?? "");
  // Seeded in `ids` order so the result's key order — and the entry for a file that yields
  // nothing — doesn't depend on which file finishes first.
  const all: AllData = {};
  for (const id of ids) all[id] = {};
  // Cap concurrency so only a few files' transient working sets (the full
  // `getAll` static + dynamic arrays, `dynamicByNode` map, and the spread
  // garbage from `mergeNode`) are alive at once — lowers the peak-memory /
  // CPU spike vs. fanning out every file with `Promise.all`. Each task writes its
  // own entry, so nothing depends on the order results come back in.
  await runWithConcurrency(
    ids.map((id) => async () => {
      all[id] = await getMergedDataForFile(id, effectiveOptions);
    }),
    5,
  );
  return all;
}

/**
 * Where a merged node falls in the order getModelPropertiesData's result iterates: the files in the order asked for,
 * then a file's nodes in the order a JS object keeps its keys (array-index keys ascending, then the others in the
 * order they were added). Compared element by element.
 */
export type NodePlace = readonly [file: number, keyKind: number, rank: number];

function comparePlaces(a: NodePlace, b: NodePlace): number {
  return a[0] - b[0] || a[1] - b[1] || a[2] - b[2];
}

/** A key a JS object orders before its other keys: the canonical form of an integer from 0 to 2^32 - 2. */
function isArrayIndexKey(key: string): boolean {
  const index = Number(key);
  return Number.isInteger(index) && index >= 0 && index < 4_294_967_295 && String(index) === key;
}

/**
 * One file's merged nodes, built as `mergeFileRows` builds them but never held: fed a batch of static rows at a time
 * in key order, then finished with the nodes only dynamic docs describe. Each node goes to `visit` with its place.
 */
export function mergedNodeWalker(
  file: number,
  dynamicRows: DynamicRow[],
  options: MergeOptions,
  visit: (node: MergedNode, place: NodePlace) => void,
  convert?: UnitConversion,
): { batch: (rows: StaticRow[]) => void; finish: () => void } {
  const dynamicByNode = groupDynamicByNode(dynamicRows);
  let added = 0;
  const placeOf = (key: string): NodePlace => (isArrayIndexKey(key) ? [file, 0, Number(key)] : [file, 1, added++]);
  return {
    batch: (rows) => {
      for (const row of rows) {
        const key = String(row.nodeId);
        const node = mergeNode(row, dynamicByNode[key] ?? [], options, convert);
        delete dynamicByNode[key];
        if (node) visit(node, placeOf(key));
      }
    },
    finish: () => {
      for (const [key, docs] of Object.entries(dynamicByNode)) {
        const node = mergeNode(undefined, docs, options, convert);
        if (node) visit(node, placeOf(key));
      }
    },
  };
}

/**
 * Distinct items in the order of their first appearance in the merged view, whatever order the nodes are walked in:
 * each item keeps the earliest place it was seen at, and `at` orders the items of one node.
 */
export function firstAppearanceCollector(): {
  node: (place: NodePlace) => void;
  add: (item: string, at: number) => void;
  ordered: () => string[];
} {
  const first = new Map<string, { place: NodePlace; at: number }>();
  let latest: NodePlace | undefined;
  let current: NodePlace = [0, 0, 0];
  let afterAll = true;
  return {
    node: (place) => {
      current = place;
      // A node past every one seen so far cannot move an item earlier: only new items count.
      afterAll = latest === undefined || comparePlaces(place, latest) > 0;
      if (afterAll) latest = place;
    },
    add: (item, at) => {
      const seen = first.get(item);
      if (!seen) first.set(item, { place: current, at });
      else if (!afterAll && (comparePlaces(current, seen.place) || at - seen.at) < 0)
        first.set(item, { place: current, at });
    },
    ordered: () =>
      [...first].sort(([, a], [, b]) => comparePlaces(a.place, b.place) || a.at - b.at).map(([item]) => item),
  };
}

/** Reads one file's static rows in key order, PROJECTED_STATIC_BATCH_SIZE at a time, handing each batch to `onBatch`. */
export function readStaticBatches(
  db: IDBDatabase,
  fileVersionId: string,
  onBatch: (rows: StaticRow[]) => void,
): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    const tx = db.transaction(STATIC_STORE, "readonly");
    const store = tx.objectStore(STATIC_STORE);
    // As in getModelPropertiesProjectedStaticData: the next batch starts just after the last key of a full one, and
    // issuing it inside `onsuccess` keeps the transaction alive until a short batch ends the walk.
    const readBatch = (lower: [string] | [string, number | string], lowerOpen: boolean) => {
      const req: IDBRequest<unknown[]> = store.getAll(
        IDBKeyRange.bound(lower, [fileVersionId, []], lowerOpen, false),
        PROJECTED_STATIC_BATCH_SIZE,
      );
      req.onsuccess = () => {
        // A batch that throws fails the read with its own error; left to escape, it would abort the transaction and
        // the read would fail with only "aborted".
        try {
          const rows = req.result;
          onBatch(asStaticRows(rows));
          const lastRow = rows.length === PROJECTED_STATIC_BATCH_SIZE ? rows[rows.length - 1] : undefined;
          if (isStaticRow(lastRow)) readBatch([fileVersionId, lastRow.nodeId], true);
        } catch (error) {
          reject(error);
          tx.abort();
        }
      };
      req.onerror = () => reject(req.error);
    };
    readBatch([fileVersionId], false);
    tx.oncomplete = () => resolve();
    tx.onabort = () => reject(new Error("IndexedDB transaction aborted", { cause: tx.error }));
  });
}

/**
 * What a streaming reader keeps of the merged view: `visit` sees each node with its place, `convert` stands in for the
 * unit conversion (see mergeNode) given the resolved options, and `result` is read once the walk is over.
 */
export interface MergedViewCollector<Result> {
  visit: (node: MergedNode, place: NodePlace) => void;
  convert: (options: MergeOptions) => UnitConversion;
  result: () => Result;
}

/**
 * Walks the merged view of the files getModelPropertiesData would read for `options`, one file and one batch of
 * static rows at a time, so no file is held whole, and returns what `collector` kept.
 */
async function walkMergedNodes<Result>(
  options: Partial<MergeOptions> & { fileVersionId?: string | string[] },
  collector: MergedViewCollector<Result>,
): Promise<Result> {
  const effectiveOptions = resolveMergeOptions(options);
  const ids =
    options.fileVersionId != null
      ? Array.isArray(options.fileVersionId)
        ? options.fileVersionId
        : [options.fileVersionId]
      : resolveFileVersionIds(store.getState().filesData, options.originalFileVersionId ?? "");
  const db = await openDB();
  const convert = collector.convert(effectiveOptions);
  // A repeated id is one entry of getModelPropertiesData's result, at its first position.
  for (const [file, id] of [...new Set(ids)].entries()) {
    const walker = mergedNodeWalker(file, await getDynamicDataForFile(id), effectiveOptions, collector.visit, convert);
    await readStaticBatches(db, id, walker.batch);
    walker.finish();
  }
  return collector.result();
}

/**
 * Keeps the property paths of the merged view, `properties.*`, `typeProperties.*` and `customProperties.*`, in the
 * order a walk over getModelPropertiesData's result first meets them (each node's properties, then its type
 * properties, then its custom properties). Conversion keeps every key, so none is converted. A bag that is not an
 * object lists nothing.
 */
export function propertyNameCollector(): MergedViewCollector<string[]> {
  const names = firstAppearanceCollector();
  return {
    visit: (node, place) => {
      names.node(place);
      let at = 0;
      for (const prefix of ["properties", "typeProperties", "customProperties"]) {
        const bag = node[prefix];
        if (isRecord(bag)) for (const key of Object.keys(bag)) names.add(`${prefix}.${key}`, at++);
      }
    },
    convert: () => (props) => props,
    result: names.ordered,
  };
}

/** The query fields of a model (propertyNameCollector) without holding the model in memory (CUBE-32604). */
export function getModelPropertyNames(
  options: Partial<MergeOptions> & { fileVersionId?: string | string[] },
): Promise<string[]> {
  return walkMergedNodes(options, propertyNameCollector());
}

/** `Array.isArray` narrows an `unknown` to `any[]`; this keeps the elements `unknown`. */
const isUnknownArray = (value: unknown): value is unknown[] => Array.isArray(value);

/**
 * A property value as the labels a value box offers: each element of a list, the `displayText` of an object (a URL
 * value), the value itself otherwise; null, undefined and an object without `displayText` offer nothing.
 */
export function propertyValueLabels(value: unknown): string[] {
  let entries: unknown[];
  if (isUnknownArray(value)) entries = value;
  else if (typeof value === "object" && value !== null)
    entries = ["displayText" in value ? value.displayText : undefined];
  else entries = [value];
  return entries.filter((entry) => entry !== undefined && entry !== null).map(String);
}

/**
 * Keeps the distinct labels of one property path (`field`, e.g. `properties.Name`) across the merged view, in the
 * order a walk over getModelPropertiesData's result first meets them, in the file's units as that walk converts them.
 * What `get(node, field)` reads of a converted bag is its `key` entry (a deeper path only walks into an object, which
 * conversion leaves as it is), so that entry alone is converted.
 */
export function propertyValueCollector(field: string): MergedViewCollector<string[]> {
  const [, key] = toPath(field);
  const labels = firstAppearanceCollector();
  return {
    visit: (node, place) => {
      labels.node(place);
      let at = 0;
      for (const label of propertyValueLabels(get(node, field))) labels.add(label, at++);
    },
    convert:
      ({ unitsNew = {}, portalUnits }) =>
      (props) => {
        if (!isRecord(props)) return {};
        const value = key === undefined ? undefined : props[key];
        if (key === undefined || typeof value !== "string") return props;
        return { [key]: updateUnits({ [key]: value }, unitsNew, portalUnits)[key] ?? value };
      },
    result: labels.ordered,
  };
}

/** One field's value suggestions (propertyValueCollector) without holding the model in memory (CUBE-32604). */
export function getModelPropertyValues(
  field: string,
  options: Partial<MergeOptions> & { fileVersionId?: string | string[] },
): Promise<string[]> {
  return walkMergedNodes(options, propertyValueCollector(field));
}

/**
 * Projected, STATIC-only read for one or more files. Unlike `getModelPropertiesData`, this never merges
 * dynamic (Mongo) data, never runs unit conversion, and never materializes the full per-node payload.
 *
 * IndexedDB has no native field projection — every read deserializes the whole stored row. So each file is
 * read in BATCHES via `getAll(range, BATCH_SIZE)` and only the requested fields are copied out per node;
 * the heavy `properties`/`typeProperties` blobs are dropped as soon as a batch is projected and the batch
 * array goes out of scope. Batching (vs. a one-row-at-a-time cursor, one IDB round-trip per node) turns ~N
 * per-row round-trips into ~N/BATCH_SIZE — the dominant win for large trees.
 *
 * @param fileVersionId  a single id or an array (merged files)
 * @param options.fields          top-level `StaticRow` keys to keep (e.g. ["parent", "cubeUniqueId"]);
 *                                `nodeId` is always included.
 * @param options.propertyFields  specific keys to pull out of `row.properties` without retaining the rest
 *                                (e.g. ["CUBE Name"]). Emitted under `properties` on each projected node.
 * @returns `{ [fvId]: { [nodeId]: projectedNode } }`
 */
export async function getModelPropertiesProjectedStaticData(
  fileVersionId: string | string[],
  options: { fields?: string[]; propertyFields?: string[] } = {},
  convertToMinifiedData = true,
): Promise<ProjectedStaticData> {
  const ids = Array.isArray(fileVersionId) ? fileVersionId : [fileVersionId];
  const all: AllData = {};

  const fileIdAndVersionIdMinMapper: FieldValueMinMapper = {};
  const cubeNameMinMapper: FieldValueMinMapper = {};

  let fileAndVersonIdCounter = 1;
  let cubeNameCounter = 1;

  if (ids.length === 0) return { data: all, fileIdAndVersionIdMinMapper, cubeNameMinMapper };

  const fields = options.fields ?? [];
  const propertyFields = options.propertyFields ?? [];
  const db = await openDB();

  const minifyData = (fieldsData: Record<string | number, unknown>, field: string, row: Record<string, unknown>) => {
    if (convertToMinifiedData) {
      switch (field) {
        case "nodeId":
        case "parent": {
          fieldsData[ObjectTreeFieldsMinValuesMapper[field]] = row[field];
          break;
        }
        case "fileId":
        case "fileVersionId": {
          const value = String(row[field]);
          if (!Object.hasOwn(fileIdAndVersionIdMinMapper, value)) {
            fileIdAndVersionIdMinMapper[value] = fileAndVersonIdCounter;
            fileAndVersonIdCounter++;
          }

          fieldsData[ObjectTreeFieldsMinValuesMapper[field]] = fileIdAndVersionIdMinMapper[value];
          break;
        }
        case "CUBE Name": {
          const value = String(row[field]);
          if (!Object.hasOwn(cubeNameMinMapper, value)) {
            cubeNameMinMapper[value] = cubeNameCounter;
            cubeNameCounter++;
          }

          fieldsData[ObjectTreeFieldsMinValuesMapper[field]] = cubeNameMinMapper[value];
          break;
        }
        case "cubeUniqueId": {
          fieldsData[ObjectTreeFieldsMinValuesMapper[field]] = row[field];
          break;
        }

        default: {
          fieldsData[field] = row[field];
        }
      }
    } else {
      fieldsData[field] = row[field];
    }
  };

  const readOne = (fvId: string) =>
    new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STATIC_STORE, "readonly");
      const store = tx.objectStore(STATIC_STORE);
      const fileNodes: FileNodes = {};

      // Project one batch's rows into `fileNodes`, keeping only the requested
      // fields so the full rows (incl. heavy `properties` blobs) can be GC'd
      // once this batch returns.
      const projectRows = (rows: StaticRow[]) => {
        for (const row of rows) {
          // Mirror the merge path's AWS filter (passesAwsFilter): keep the root
          // (nodeId 0) and any node with a parent; skip orphan non-root rows so
          // the projected result matches what `getMergedDataForFile` would return.
          if (!passesAwsFilter(row)) continue;

          // Project only the requested fields. `nodeId` is always kept so the
          // node stays identifiable.
          const projected: MergedNode = {};
          minifyData(projected, "nodeId", row);
          for (const f of fields) {
            if (f in row) minifyData(projected, f, row);
          }
          if (propertyFields.length && row.properties) {
            const rowProps = row.properties;
            for (const pf of propertyFields) {
              if (pf in rowProps) minifyData(projected, pf, rowProps);
            }
          }

          fileNodes[String(row.nodeId)] = projected;
        }
      };

      // Walk the file's contiguous `[fvId, nodeId]` key range in
      // PROJECTED_STATIC_BATCH_SIZE chunks — one `getAll` round-trip per batch
      // instead of one per row. The next batch starts just after the last key
      // we saw (`lowerOpen`), so no row is read twice. Re-issuing `getAll` on the
      // same store synchronously inside `onsuccess` keeps the transaction alive
      // until a short (final) batch, at which point `oncomplete` fires.
      const readBatch = (lower: [string] | [string, number | string], lowerOpen: boolean) => {
        const range = IDBKeyRange.bound(lower, [fvId, []], lowerOpen, false);
        const req: IDBRequest<unknown[]> = store.getAll(range, PROJECTED_STATIC_BATCH_SIZE);
        req.onsuccess = () => {
          // As in readStaticBatches: a batch that throws fails the read with its own error, not the abort it causes.
          try {
            const rows = req.result;
            projectRows(asStaticRows(rows));
            if (rows.length === PROJECTED_STATIC_BATCH_SIZE) {
              // Pagination keys off the RAW batch length, so a row the guard rejected can never cut
              // the walk short; the last row of a full batch is keyed, so it carries the next lower
              // bound.
              const lastRow = rows[rows.length - 1];
              if (isStaticRow(lastRow)) readBatch([fvId, lastRow.nodeId], true);
            }
          } catch (error) {
            reject(error);
            tx.abort();
          }
        };
        req.onerror = () => reject(req.error);
      };

      readBatch([fvId], false);

      tx.oncomplete = () => {
        if (convertToMinifiedData) {
          let mappedId = fileIdAndVersionIdMinMapper[fvId];
          if (mappedId === undefined) {
            mappedId = fileAndVersonIdCounter++;
            fileIdAndVersionIdMinMapper[fvId] = mappedId;
          }
          all[mappedId] = fileNodes;
        } else {
          all[fvId] = fileNodes;
        }
        resolve();
      };
      tx.onabort = () => reject(new Error("IndexedDB transaction aborted", { cause: tx.error }));
    });

  // Cap concurrency to bound how many files' batches are in flight at once. Each
  // in-flight file transiently holds up to PROJECTED_STATIC_BATCH_SIZE
  // freshly-deserialized rows (until they're projected + GC'd) plus its slim
  // accumulator, so the peak working set is ~concurrency × batch-size rows.
  await runWithConcurrency(
    ids.map((id) => () => readOne(id)),
    5,
  );

  return { data: all, fileIdAndVersionIdMinMapper, cubeNameMinMapper };
}

/**
 * Clears every file except those in `keep` — used by Settings > Clear Cache.
 *
 * Key-only cursors (never the row payload, so no row data is loaded). The static store's compound primary
 * key `[fileVersionId, nodeId]` keeps a file's rows contiguous, so this advances ONE step per file —
 * jumping file boundaries with `cursor.continue([fileVersionId, []])` — and drops a non-kept file's whole
 * range in a single `delete(staticFileRange)`, i.e. O(files) not O(nodes). The dynamic store
 * (autoIncrement key) must delete per doc by primary key via its `fileVersionId` index, but skips a kept
 * file's entries in one jump.
 */
export async function clearOtherFilesModelPropertiesData(keepFileVersionIds: string[]): Promise<void> {
  const keep = new Set(keepFileVersionIds);
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction([STATIC_STORE, DYNAMIC_STORE, META_STORE], "readwrite");

    const metaStore = tx.objectStore(META_STORE);
    const metaCursorReq = metaStore.openKeyCursor();
    metaCursorReq.onsuccess = () => {
      const cursor = metaCursorReq.result;
      if (!cursor) return;
      if (!keep.has(String(cursor.key))) metaStore.delete(cursor.key);
      cursor.continue();
    };

    // Static store: step once per file, not per node. `[fileVersionId, []]`
    // sorts after every `[fileVersionId, nodeId]` (an array orders after any
    // number/string), so `continue([fileVersionId, []])` lands on the next
    // file's first row — skipping the rest of this file's nodes. Non-kept files
    // are removed with a single range delete instead of one delete per row.
    const staticStore = tx.objectStore(STATIC_STORE);
    const staticCursorReq = staticStore.openKeyCursor();
    staticCursorReq.onsuccess = () => {
      const cursor = staticCursorReq.result;
      if (!cursor) return;
      // Primary key is the compound `[fileVersionId, nodeId]` (see the store's keyPath).
      const fileVersionId = Array.isArray(cursor.key) ? String(cursor.key[0]) : String(cursor.key);
      if (!keep.has(fileVersionId)) {
        staticStore.delete(staticFileRange(fileVersionId));
      }
      cursor.continue([fileVersionId, []]);
    };

    // Dynamic store: autoIncrement primary key, so deletes go by primary key via
    // the `fileVersionId` index (no range delete by index). Non-kept docs are
    // walked + deleted individually; a KEPT file's entries are skipped in one
    // jump — `fileVersionId + "\u0000"` is the smallest key past all of them.
    const dynamicStore = tx.objectStore(DYNAMIC_STORE);
    const dynamicCursorReq = dynamicStore.index("fileVersionId").openKeyCursor();
    dynamicCursorReq.onsuccess = () => {
      const cursor = dynamicCursorReq.result;
      if (!cursor) return;
      // The `fileVersionId` index keys on that string field.
      const fileVersionId = String(cursor.key);
      if (keep.has(fileVersionId)) {
        cursor.continue(fileVersionId + "\u0000");
      } else {
        dynamicStore.delete(cursor.primaryKey);
        cursor.continue();
      }
    };

    tx.oncomplete = () => resolve();
    tx.onabort = () => reject(new Error("IndexedDB transaction aborted", { cause: tx.error }));
  });
}

// ----------------------------------------------------------------------------
// Backward-compat: one shim retained — only `createAssetDataPayload` in
// actions/Viewer.ts still goes through this name. Routes to the new
// per-node merged-read API.
// ----------------------------------------------------------------------------

export async function getNodeModelPropertiesData(
  fileVersionId: string,
  nodeId: number | string,
  options?: MergeOptions,
): Promise<MergedNode | undefined> {
  const result = await getMergedDataForNode(fileVersionId, nodeId, options);
  return result[String(nodeId)];
}
